/**
 * P-8 — comment nommer la version de catalogue figée d'une campagne, à l'écran.
 *
 * Trois surfaces admin affichaient l'uuid de la version (tronqué à 8 caractères) là où l'utilisateur
 * attend son NUMÉRO. L'API le fournit désormais (`catalogVersionNumber`, résolu par port ACL), mais le
 * champ est OPTIONNEL par contrat : port non câblé, version supprimée du référentiel, réponse d'une API
 * antérieure au lot. Ce helper porte la règle de repli UNE fois, pour que les trois surfaces ne la
 * réinventent pas chacune à sa façon.
 *
 * `isFallback` existe pour que l'appelant puisse HABILLER différemment les deux cas : un numéro de version
 * est du texte (police courante), un uuid tronqué est un identifiant technique (chasse fixe). Rendre un
 * numéro en `font-mono` le ferait passer pour un code ; rendre un hash en police courante le ferait passer
 * pour un nom.
 */
export interface CatalogVersionLabel {
  /** Ce qu'on affiche : le numéro de version, ou à défaut l'uuid tronqué. */
  readonly text: string;
  /** `true` ⇒ le numéro n'était pas disponible, `text` est un identifiant technique. */
  readonly isFallback: boolean;
}

/**
 * @param catalogVersionId identité de la version (toujours présente) — sert de repli ET d'infobulle.
 * @param catalogVersionNumber numéro humain, absent si l'API n'a pas su le résoudre.
 */
export function catalogVersionLabel(
  catalogVersionId: string,
  catalogVersionNumber?: string,
): CatalogVersionLabel {
  // Chaîne vide traitée comme absente : un libellé vide afficherait un blanc inexplicable, alors que le
  // hash, lui, permet au moins d'identifier la version auprès du support.
  if (catalogVersionNumber !== undefined && catalogVersionNumber.trim() !== '') {
    return { text: catalogVersionNumber, isFallback: false };
  }
  return { text: catalogVersionId.slice(0, 8), isFallback: true };
}
